import React, { useState, useCallback, createContext, useEffect } from 'react';
import { Button, Form, message, Select, UploadFile } from 'antd';
import { useSelector } from 'react-redux';
import client from '@/lib/feathers';
import ConfirmModal from '../ConfirmModal';
import UploadLogo from '../UploadLogo';
import ThemeControlBtns from '../ThemeControlBtns';
import { mainColorPickers, subColorPickers, icons } from './constants';
import styles from './index.module.scss';
import type { User } from '@/types/database/users';
import type { RootState } from '@/redux/store';
import type { Theme, ThemeSchema } from '../themeSchema';

type ColorSettingFormProps = {
  themes: Theme[];
  selectedTheme: Theme;
  updateThemes: (newThemes: Theme[]) => void;
  updateSelectedTheme: (newTheme: Theme) => void;
};

export const IsErrorContext = createContext(false);

const DEFAULT_LOGO_URL = '/ezcon/icon/pink-one/logo.svg';

const ColorSettingForm = ({
  themes,
  selectedTheme,
  updateThemes,
  updateSelectedTheme,
}: ColorSettingFormProps) => {
  const user = useSelector<RootState>((state) => state.user.auth.user) as User;

  const [communityThemeName, setCommunityThemeName] = useState<string>('預設');
  const [isThemeSelected, setIsThemeSelected] = useState<boolean>(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState<boolean>(false);
  const [isColorPicked, setIsColorPicked] = useState<boolean>(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [themeName, setThemeName] = useState<string>('');
  const [isInputError, setIsInputError] = useState<boolean>(false);

  useEffect(() => {
    const getCommunityThemeName = async () => {
      const res = await client.service('communityData').find();
      const themeName: string = res.data[0].themeName;
      setCommunityThemeName(themeName);
    };
    getCommunityThemeName();
  }, []);

  const addFile = (file: UploadFile) => {
    setFileList([file]);
  };
  const removeFile = () => {
    setFileList([]);
  };

  const getLogoUrl = (logoUrl: string): void => {
    const newTheme = JSON.parse(JSON.stringify(selectedTheme));

    if (logoUrl !== DEFAULT_LOGO_URL) {
      newTheme.isUploaded = true;
    } else {
      newTheme.isUploaded = false;
    }
    newTheme.logoUrl = logoUrl;
    updateSelectedTheme(newTheme);
  };

  const changeTheme = (value: string) => {
    if (isThemeSelected === false && value !== communityThemeName) {
      setIsThemeSelected(true);
    }
    if (isThemeSelected && value === communityThemeName) {
      setIsThemeSelected(false);
    }
    const newSelectedTheme = themes.find((theme) => {
      return theme.name === value;
    });

    setIsColorPicked(false);
    if (newSelectedTheme) {
      updateSelectedTheme(newSelectedTheme);

      if (fileList.length !== 0) {
        setFileList([]);
        client.service('uploads').remove(fileList[0].response._id);
      }
    }
  };

  const updateCommunityTheme = async () => {
    const res = await client.service('communityData').find();
    await client.service('communityData').patch(res.data[0]._id, {
      themeName: selectedTheme.name,
    });
    setCommunityThemeName(selectedTheme.name);
    message.success('主題套用成功！');
    setIsThemeSelected(false);
  };

  const changeIcons = (iconsName: string) => {
    updateSelectedTheme({ ...selectedTheme, iconsName });
  };

  const themesFilter = useCallback(
    (logoUrl?: string): Theme[] => {
      const index = themes.findIndex((theme) => {
        return theme.name === selectedTheme.name;
      });
      const filteredThemes = themes.filter((theme) => {
        return theme.name !== selectedTheme.name;
      });
      if (logoUrl) {
        if (logoUrl !== DEFAULT_LOGO_URL) {
          filteredThemes.splice(index, 0, {
            ...selectedTheme,
            logoUrl,
            isUploaded: true,
          });
        } else {
          filteredThemes.splice(index, 0, {
            ...selectedTheme,
            logoUrl,
            isUploaded: false,
          });
        }
      } else {
        filteredThemes.splice(index, 0, selectedTheme);
      }

      return filteredThemes;
    },
    [selectedTheme, themes]
  );

  const toBeThemeSchema = useCallback(
    (themeObj: Theme): ThemeSchema => {
      const {
        name,
        bgColor,
        sysColor,
        primaryBtnGradientStart,
        primaryBtnGradientEnd,
        primaryBtnTxt,
        secondaryBtnBg,
        secondaryBtnTxt,
        iconsName,
        logoUrl,
        official,
      } = themeObj;
      const formattedThemes = {
        user: user.email,
        name: name,
        color: {
          bg: bgColor,
          system: sysColor,
          button: {
            primary: {
              gradient1: primaryBtnGradientStart,
              gradient2: primaryBtnGradientEnd,
              text: primaryBtnTxt,
            },
            secondary: {
              bg: secondaryBtnBg,
              text: secondaryBtnTxt,
            },
          },
        },
        icons: {
          navbar: { name: iconsName },
          logo: { url: logoUrl },
        },
        official,
      };
      return formattedThemes;
    },
    [user.email]
  );

  const colorPickerChangeHandler = (
    colorValue: string,
    colorType: string
  ): void => {
    const changedTheme = { ...selectedTheme, [colorType]: colorValue };
    if (isColorPicked === false) {
      setIsColorPicked(true);
      updateSelectedTheme(changedTheme);
    }
    updateSelectedTheme(changedTheme);
  };

  const resetTheme = () => {
    const originalTheme = themes.find((theme) => {
      return theme.name === selectedTheme.name;
    });

    if (originalTheme) {
      updateSelectedTheme(originalTheme);
    }

    setIsColorPicked(false);

    if (fileList.length !== 0) {
      setFileList([]);
      client.service('uploads').remove(fileList[0].response._id);
    }
  };

  const checkThemeNameIsExist = useCallback((): boolean => {
    return themes.some((theme) => themeName === theme.name);
  }, [themeName, themes]);

  const addTheme = useCallback(async () => {
    const newTheme: Theme = JSON.parse(JSON.stringify(selectedTheme));
    newTheme.name = themeName;
    newTheme.official = false;
    if (newTheme.logoUrl !== DEFAULT_LOGO_URL) {
      newTheme.isUploaded = false;
    }
    const newThemes = JSON.parse(JSON.stringify(themes));
    newThemes.push(newTheme);

    updateThemes(newThemes);
    updateSelectedTheme(newTheme);
    setIsColorPicked(false);
    setThemeName('');

    await client.service('communityAppTheme').create(
      toBeThemeSchema({
        ...selectedTheme,
        name: themeName,
        official: false,
      })
    );

    setFileList([]);

    message.success('主題新增成功！');
    setIsThemeSelected(true);
  }, [
    selectedTheme,
    themeName,
    themes,
    toBeThemeSchema,
    updateSelectedTheme,
    updateThemes,
  ]);

  const updateTheme = useCallback(
    async (logoUrl?: string) => {
      if (logoUrl) {
        const filteredThemes = themesFilter(logoUrl);
        updateThemes(filteredThemes);

        const { data } = await client
          .service('communityAppTheme')
          .find({ query: { name: selectedTheme.name } });

        await client
          .service('communityAppTheme')
          .update(data[0]._id, toBeThemeSchema({ ...selectedTheme, logoUrl }));
      } else {
        const filteredThemes = themesFilter();
        updateThemes(filteredThemes);

        const { data } = await client
          .service('communityAppTheme')
          .find({ query: { name: selectedTheme.name } });

        await client
          .service('communityAppTheme')
          .update(data[0]._id, toBeThemeSchema(selectedTheme));
        message.success('主題設定更新成功！');
      }
      if (fileList.length !== 0) {
        setFileList([]);
      }
    },
    [
      fileList.length,
      selectedTheme,
      themesFilter,
      toBeThemeSchema,
      updateThemes,
    ]
  );

  const deleteTheme = useCallback(async () => {
    const filteredThemes = themes.filter((theme) => {
      return theme.name !== selectedTheme.name;
    });
    updateThemes(filteredThemes);
    if (selectedTheme.name === communityThemeName) {
      updateSelectedTheme(filteredThemes[0]);
    } else {
      const communityTheme = filteredThemes.find(
        (theme) => theme.name === communityThemeName
      ) as Theme;
      updateSelectedTheme(communityTheme);
    }
    setIsColorPicked(false);

    const { data } = await client
      .service('communityAppTheme')
      .find({ query: { name: selectedTheme.name } });

    if (selectedTheme.logoUrl !== '/ezcon/icon/pink-one/logo.svg') {
      const { data: dbLogoImg } = await client
        .service('uploads')
        .find({ query: { showPath: selectedTheme.logoUrl } });
      await client.service('uploads').remove(dbLogoImg[0]._id);
    }

    await client.service('communityAppTheme').remove(data[0]._id);

    if (selectedTheme.name === communityThemeName) {
      const res = await client.service('communityData').find();
      await client.service('communityData').patch(res.data[0]._id, {
        themeName: filteredThemes[0].name,
      });
      setCommunityThemeName(filteredThemes[0].name);
    }
    setIsThemeSelected(false);

    message.success('主題刪除成功！');
  }, [
    communityThemeName,
    selectedTheme,
    themes,
    updateSelectedTheme,
    updateThemes,
  ]);

  const updateNewThemeName = useCallback((name: string) => {
    setThemeName(name);
  }, []);

  const checkThemeName = useCallback((): boolean => {
    if (themeName.trim() === '') {
      setIsInputError(true);
      return false;
    } else {
      if (checkThemeNameIsExist() === false) {
        setIsInputError(false);
        return true;
      } else {
        setIsInputError(true);
        return false;
      }
    }
  }, [checkThemeNameIsExist, themeName]);

  const updateInputError = useCallback((flag: boolean) => {
    setIsInputError(flag);
  }, []);

  const handleCreateModalClose = () => {
    setIsCreateModalOpen(false);
    setThemeName('');
  };

  const confirmModalOtherProps = {
    isInputError,
    themeName,
    checkThemeNameIsExist,
    updateNewThemeName,
    updateInputError,
  };

  return (
    <div className={styles.formWrapper}>
      <div className={styles.selectedTheme}>
        <span>
          當前主題：
          <span className={styles.currentTheme}>{selectedTheme.name}</span>
        </span>
        <Button
          type="primary"
          disabled={!isThemeSelected}
          onClick={updateCommunityTheme}
        >
          套用此主題
        </Button>
      </div>
      <Form
        labelCol={{
          span: 6,
        }}
        layout="vertical"
      >
        <Button
          type="primary"
          disabled={
            isColorPicked === false && selectedTheme.isUploaded === false
          }
          onClick={() => setIsCreateModalOpen(true)}
          className={styles.createThemeBtn}
        >
          新增主題
        </Button>
        <ConfirmModal
          action="create"
          title="新增目前設定為新主題？"
          content="請輸入新的主題名稱後，再按確定鍵。"
          open={isCreateModalOpen}
          confirmModalOtherProps={confirmModalOtherProps}
          checkThemeName={checkThemeName}
          confirmCallback={addTheme}
          setModalClose={handleCreateModalClose}
        />
        <Form.Item label="主題選擇:" className={styles.selectTitle}>
          <Select
            value={selectedTheme.name}
            placeholder="請選擇主題"
            onChange={(value) => {
              changeTheme(value);
            }}
            options={themes.map((theme) => {
              return {
                label: theme.name,
                value: theme.name,
              };
            })}
          />
        </Form.Item>
        <div className={styles.colorPickerWrapper}>
          {Object.keys(mainColorPickers).map((key) => {
            const { id, text } = mainColorPickers[key];
            const spanClass = mainColorPickers[key]['spanClass'];
            const inputClass = mainColorPickers[key]['inputClass'];
            return (
              <div className={styles.colorPickerContainer} key={key}>
                <label htmlFor={id}>
                  <span className={spanClass !== '' ? styles[spanClass] : ''}>
                    {text}
                  </span>
                </label>
                <input
                  className={
                    inputClass ? styles.inputStyles : styles.colorPicker
                  }
                  type="color"
                  value={selectedTheme[id]}
                  onChange={(event) => {
                    colorPickerChangeHandler(event.target.value, id);
                  }}
                  id={id}
                />
              </div>
            );
          })}
        </div>
        <div className={styles.subColorPickerWrapper}>
          {Object.keys(subColorPickers).map((key) => {
            const { spanClass, id, text } = subColorPickers[key];
            return (
              <div className={styles.colorPickerContainer} key={key}>
                <label htmlFor={id}>
                  <span className={styles[spanClass]}>{text}</span>
                </label>
                <input
                  className={styles.colorPicker}
                  type="color"
                  value={selectedTheme[id]}
                  onChange={(event) => {
                    colorPickerChangeHandler(event.target.value, id);
                  }}
                  id={id}
                />
              </div>
            );
          })}
        </div>
        <Form.Item
          className={styles.iconsTitle}
          label="導覽列icon選擇:"
          labelCol={{ span: 12 }}
        >
          <Select
            defaultValue="預設"
            options={icons}
            className={styles.iconSelector}
            onSelect={(iconsName: string) => changeIcons(iconsName)}
          />
        </Form.Item>
        <UploadLogo
          fileList={fileList}
          getLogoUrl={getLogoUrl}
          addFile={addFile}
          removeFile={removeFile}
        />
      </Form>
      <ThemeControlBtns
        isOfficial={selectedTheme.official}
        isColorPicked={isColorPicked}
        updateTheme={updateTheme}
        deleteTheme={deleteTheme}
        resetTheme={resetTheme}
        confirmModalOtherProps={confirmModalOtherProps}
      />
    </div>
  );
};

export default ColorSettingForm;
